import React, { useState, useEffect } from "react";
import { Card, Placeholder, Table } from "react-bootstrap";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import { useFinanceCategory } from "@features/admin/finance/category/hooks/useFinanceCategory";
import { useFinanceCategoryAction } from "@features/admin/finance/category/hooks/useFinanceCategoryAction";
import { FinanceCategory } from "@features/admin/finance/category/types/financeCategory";
import DeductibleSummary, { deductibleSummaryText } from "@features/admin/finance/components/DeductibleSummary";
import { showSaveWarnings } from "@features/admin/finance/hooks/useDeductibleConfigForm";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useDebounce } from "@core/hooks/useDebounce";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import NoResult from "@core/components/NoResult";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useEntityContext, usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES } from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import UpdateFinanceCategoryModal from "./UpdateFinanceCategoryModal";

const CATEGORY_FILTER_FIELDS: FilterField[] = [
    {
        name: "isActive",
        label: "Status",
        type: "select",
        options: [
            { value: "1", label: "Active" },
            { value: "0", label: "Inactive" },
        ],
    },
];

const CATEGORY_PERMISSION_CODES = [
    ACTION_CODES.UPDATE_CHURCH_FINANCIAL_CATEGORY,
    ACTION_CODES.DISABLE_CHURCH_FINANCIAL_CATEGORY,
    ACTION_CODES.ENABLE_CHURCH_FINANCIAL_CATEGORY,
];

type StatusConfirmState = {
    category: FinanceCategory;
    kind: StatusChangeKind;
};

interface FinanceCategoryTableProps {
    showPagination?: boolean;
    refreshSignal?: number;
}

const FinanceCategoryTable: React.FC<FinanceCategoryTableProps> = ({ showPagination = false, refreshSignal }) => {
    const [searchParams] = useSearchParams();
    const resolvedNationId = useScopeId() ?? 0;
    const { context } = useEntityContext();

    const [page, setPage] = useState(() => Number(searchParams.get("page") || "1"));
    const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
    const debouncedSearch = useDebounce(search, 300);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const result: Record<string, any> = {};
        searchParams.forEach((value, key) => {
            if (key !== "q" && key !== "page") result[key] = value;
        });
        return result;
    });

    const [selectedCategory, setSelectedCategory] = useState<FinanceCategory | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<StatusConfirmState | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);

    const showError = useApiError();
    const { doDisable, doEnable } = useFinanceCategoryAction();

    const { hasPermission } = usePermissions({
        scopeType: context?.scope,
        scopeId: context?.scopeId,
        actionCodes: CATEGORY_PERMISSION_CODES,
    });

    const canEdit = hasPermission(ACTION_CODES.UPDATE_CHURCH_FINANCIAL_CATEGORY);
    const canDisable = hasPermission(ACTION_CODES.DISABLE_CHURCH_FINANCIAL_CATEGORY);
    const canEnable = hasPermission(ACTION_CODES.ENABLE_CHURCH_FINANCIAL_CATEGORY);

    useSearchUrlSync({ query: debouncedSearch, filters: activeFilters, page });

    const isActiveFilter =
        activeFilters.isActive === "1" ? true :
        activeFilters.isActive === "0" ? false :
        undefined;

    const { current_page, data, from, last_page, per_page, total, loading, reload } = useFinanceCategory({
        nationId: resolvedNationId,
        page,
        ...(debouncedSearch ? { freetext: debouncedSearch } : {}),
        ...(isActiveFilter !== undefined ? { isActive: isActiveFilter } : {}),
    });

    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({ from, page: pageNumber, perPage: per_page, total, currentCount: data.length });

    // Reload when a sibling create succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    useEffect(() => {
        if (selectedCategory) setShowEditModal(true);
    }, [selectedCategory]);

    useEffect(() => { setPage(1); }, [debouncedSearch]);

    const formatRange = (cat: FinanceCategory) => {
        const fromVal = cat.range_from ? `$${Number(cat.range_from).toLocaleString()}` : "—";
        const toVal = cat.range_to ? ` – $${Number(cat.range_to).toLocaleString()}` : "+";
        return `${fromVal}${toVal}`;
    };

    const openEdit = (category: FinanceCategory) => {
        setSelectedCategory(category);
    };

    const openStatusConfirm = (category: FinanceCategory, kind: StatusChangeKind) => {
        setStatusConfirm({ category, kind });
    };

    const closeStatusConfirm = () => {
        if (!statusBusy) setStatusConfirm(null);
    };

    const handleStatusConfirm = async () => {
        if (!statusConfirm) return;
        const { category, kind } = statusConfirm;
        setStatusBusy(true);
        try {
            const response = kind === "disable"
                ? await doDisable(resolvedNationId, category.category_group_id)
                : await doEnable(resolvedNationId, category.category_group_id);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "");
                showSaveWarnings(response.getData());
                setStatusConfirm(null);
                await reload();
            } else {
                showError(response.getError() as ErrorResponse);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        } finally {
            setStatusBusy(false);
        }
    };

    // Shared by the desktop table row and the mobile card.
    const renderCategoryActions = (r: (typeof data)[number]) => (
        <TableActions aria-label={`Actions for ${r.category_name}`}>
            {canEdit && (
                <TableActionButton
                    icon={<FiEdit2 />}
                    label="Edit"
                    primary
                    onClick={() => openEdit(r)}
                    data-testid={`category-edit-${r.category_group_id}`}
                />
            )}
            {r.is_active && canDisable && (
                <TableActionButton
                    icon={<FiSlash />}
                    label="Disable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--danger"
                    onClick={() => openStatusConfirm(r, "disable")}
                    data-testid={`category-disable-${r.category_group_id}`}
                />
            )}
            {!r.is_active && canEnable && (
                <TableActionButton
                    icon={<FiCheckCircle />}
                    label="Enable"
                    variant="outline-secondary"
                    className="ntcog-table-action-btn--success"
                    onClick={() => openStatusConfirm(r, "enable")}
                    data-testid={`category-enable-${r.category_group_id}`}
                />
            )}
        </TableActions>
    );

    return (
        <div data-testid="finance-category-table">
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search categories…"
                    filterFields={CATEGORY_FILTER_FIELDS}
                    initialQuery={search}
                    onQueryChange={(q) => { setSearch(q); setPage(1); }}
                    onFiltersApply={(f) => { setActiveFilters(f); setPage(1); }}
                    onClear={() => { setSearch(""); setActiveFilters({}); setPage(1); }}
                    loading={loading}
                    activeFilterCount={Object.values(activeFilters).filter(Boolean).length}
                    testId="finance-category-search"
                />
            </div>

            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="finance-category-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive hover size="sm" className="mb-0 align-middle table-striped ntcog-table">
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th className="d-none d-md-table-cell">#</th>
                                    <th>Category Name</th>
                                    <th className="d-none d-md-table-cell">Range</th>
                                    <th className="d-none d-md-table-cell">Allocation</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody
                                className="table-group-divider"
                                style={{ opacity: loading && data.length > 0 ? 0.5 : 1, transition: "opacity 0.2s ease" }}
                            >
                                {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`}>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={3} /></td>
                                        <td><Placeholder xs={6} /></td>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={5} /></td>
                                        <td className="d-none d-md-table-cell"><Placeholder xs={3} /></td>
                                        <td><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-5">
                                            <NoResult
                                                line_1="No finance categories found."
                                                line_2="Try adjusting filters or create a new category."
                                            />
                                        </td>
                                    </tr>
                                )}
                                {data.length > 0 && data.map((r, i) => {
                                    const rowNumber = from != null ? from + i : i + 1;

                                    return (
                                        <tr key={r.category_group_id} data-testid={`category-row-${r.category_group_id}`} className="bg-white">
                                            <td className="text-muted small d-none d-md-table-cell">{rowNumber}</td>
                                            <td className="fw-semibold">{r.category_name}</td>
                                            <td className="small d-none d-md-table-cell">{formatRange(r)}</td>
                                            <td className="small d-none d-md-table-cell">
                                                <DeductibleSummary deductibles={r.deductibles} testId={`category-allocation-${r.category_group_id}`} />
                                            </td>
                                            <td>
                                                <StatusPill active={r.is_active} />
                                            </td>
                                            <td className="text-end">
                                                {renderCategoryActions(r)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="finance-category-cards">
                        {loading && data.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`category-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult
                                line_1="No finance categories found."
                                line_2="Try adjusting filters or create a new category."
                            />
                        )}
                        {data.length > 0 && data.map((r) => (
                            <div key={r.category_group_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`category-card-${r.category_group_id}`}
                                    ariaLabel={`Category ${r.category_name}`}
                                    title={<span className="fw-semibold">{r.category_name}</span>}
                                    status={<StatusPill active={r.is_active} />}
                                    actions={renderCategoryActions(r)}
                                    details={[
                                        { label: "Range", value: formatRange(r) },
                                        {
                                            label: "Allocation",
                                            value: deductibleSummaryText(r.deductibles),
                                        },
                                    ]}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="finance-category-pagination"
                    info={`Showing ${start} to ${end} of ${total} categories`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateFinanceCategoryModal
                show={showEditModal}
                onHide={() => { setShowEditModal(false); setSelectedCategory(null); }}
                category={selectedCategory}
                onSuccess={reload}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="category"
                entityName={statusConfirm?.category.category_name ?? ""}
                onConfirm={handleStatusConfirm}
                onCancel={closeStatusConfirm}
                busy={statusBusy}
            />
        </div>
    );
};

export default FinanceCategoryTable;
